

import * as React from "react"
import { Plus, X, Globe, Lock, Edit } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import type { URL } from "@/lib/types"
import { ConfirmationDialog } from "@/components/shared/confirmation-dialog"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

interface URLsProps {
  onAddClick: () => void
  urls: URL[]
  loading: boolean
  error: string | null
  onDelete: (id: string) => Promise<void>
  onUpdate?: (id: string, data: Partial<URL>) => Promise<void>
  categories?: { id: string; name: string; subcategories: { id: string; name: string }[] }[]
  translations?: Record<string, unknown>
  onRefreshCategories?: () => Promise<void>
}

export function URLs({ onAddClick, urls, loading, error, onDelete, onUpdate, categories, translations, onRefreshCategories }: URLsProps) {
  // Translation helper function
  const t = (key: string): string => {
    if (!translations) return key
    const result = key.split('.').reduce((obj: Record<string, unknown>, k: string) => obj?.[k] as Record<string, unknown>, translations as Record<string, unknown>)
    return typeof result === 'string' ? result : key
  }
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)
  const [itemToDelete, setItemToDelete] = React.useState<URL | null>(null)
  
  // Edit state
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [editingURL, setEditingURL] = React.useState<URL | null>(null)
  const [editForm, setEditForm] = React.useState({
    title: '',
    description: '',
    url: '',
    category: '',
    subcategory: ''
  })
  const [updatingId, setUpdatingId] = React.useState<string | null>(null)

  const handleDeleteClick = (url: URL) => {
    setItemToDelete(url)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return
    
    setDeletingId(itemToDelete.id)
    try {
      await onDelete(itemToDelete.id)
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    } catch (error) {
      console.error('Error deleting URL:', error)
    } finally {
      setDeletingId(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setItemToDelete(null)
    setDeletingId(null)
  }

  const handleEditClick = async (url: URL) => {
    setEditingURL(url)
    
    // Refresh categories before opening the dialog
    if (onRefreshCategories) {
      try {
        await onRefreshCategories()
      } catch (error) {
        console.warn('Failed to refresh categories:', error)
      }
    }
    
    // Find category and subcategory IDs based on names
    const categoryObj = categories?.find(cat => cat.name === url.category)
    const subcategoryObj = categoryObj?.subcategories.find(sub => sub.name === url.subcategory)
    
    setEditForm({
      title: url.title,
      description: url.description || '',
      url: url.url,
      category: categoryObj?.id || '',
      subcategory: subcategoryObj?.id || ''
    })
    setEditDialogOpen(true)
  }

  const handleEditSubmit = async () => {
    if (!editingURL || !onUpdate) return
    
    setUpdatingId(editingURL.id)
    try {
      await onUpdate(editingURL.id, {
        title: editForm.title,
        description: editForm.description,
        url: editForm.url,
        category: editForm.category,
        subcategory: editForm.subcategory
      })
      setEditDialogOpen(false)
      setEditingURL(null)
    } catch (error) {
      console.error('Error updating URL:', error)
    } finally {
      setUpdatingId(null)
    }
  }

  const handleCancelEdit = () => {
    setEditDialogOpen(false)
    setEditingURL(null)
    setUpdatingId(null)
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('knowledge.filters.urls')}</CardTitle>
          <CardDescription>{t('knowledge.urls_description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
            <CardTitle>URLs</CardTitle>
        </CardHeader>
        <CardContent>
            <p className="text-destructive">Error loading URLs: {error}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>URLs</CardTitle>
          <CardDescription>{t('knowledge.urls_description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button className="w-full" onClick={onAddClick}>
            <Plus className="mr-2 h-4 w-4" /> {t('knowledge.add_url')}
          </Button>

          <div className="space-y-2">
            {urls.map((url) => (
              <div key={url.id} className="flex flex-col space-y-2 rounded-lg border p-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-2">
                    {url.isPublic ? <Globe className="mt-1 h-4 w-4" /> : <Lock className="mt-1 h-4 w-4" />}
                    <div className="flex flex-col">
                      <span className="font-medium">{url.title}</span>
                      <span className="text-sm text-muted-foreground">{url.url}</span>
                      <p className="mt-1 text-sm text-muted-foreground">{url.description}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    {onUpdate && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditClick(url)}
                        disabled={updatingId === url.id}
                        className="text-primary hover:bg-primary/10 hover:text-primary"
                      >
                        <Edit className="mr-1 h-3 w-3" />
                        Edit
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDeleteClick(url)}
                      disabled={deletingId === url.id}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                  <span>{url.category}</span>
                  <span>/</span>
                  <span>{url.subcategory}</span>
                </div>
              </div>
            ))}
             {urls.length === 0 && !loading && (
              <div className="py-4 text-center text-muted-foreground">
                No URLs found.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('knowledge.delete_confirmation.title')}
        message={itemToDelete ? (t('knowledge.delete_confirmation.url_message') as string).replace('{{title}}', itemToDelete.title) : ''}
        description={t('knowledge.delete_confirmation.description')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={deletingId !== null}
        translations={t}
      />

      {/* Edit URL Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit URL
            </DialogTitle>
            <DialogDescription>
              Update the URL information below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* URL Info Section */}
            {editingURL && (
              <div className="grid gap-4 rounded-lg bg-muted/50 p-4">
                <h4 className="text-sm font-medium">URL Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <span className="ml-2 font-medium">
                      {editingURL.isPublic ? (
                        <span className="flex items-center gap-1 text-success">
                          <Globe className="h-3 w-3" />
                          Public
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-warning">
                          <Lock className="h-3 w-3" />
                          Private
                        </span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Added:</span>
                    <span className="ml-2 font-medium">
                      {editingURL.addedDate ? new Date(editingURL.addedDate).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Form */}
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="URL title"
                  className="font-medium"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="url">URL *</Label>
                <Input
                  id="url"
                  value={editForm.url}
                  onChange={(e) => setEditForm(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com"
                  className="font-mono text-sm"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="URL description"
                  rows={4}
                />
              </div>
              {categories && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={editForm.category}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, category: value, subcategory: '' }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="subcategory">Subcategory *</Label>
                    <Select
                      value={editForm.subcategory}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, subcategory: value }))}
                      disabled={!editForm.category}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subcategory" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories
                          .find(cat => cat.id === editForm.category)
                          ?.subcategories.map((sub) => (
                            <SelectItem key={sub.id} value={sub.id}>
                              {sub.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end space-x-2 border-t pt-4">
            <Button variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSubmit} 
              disabled={updatingId !== null || !editForm.title || !editForm.url || !editForm.category || !editForm.subcategory}
              className=""
            >
              {updatingId ? 'Updating...' : 'Update URL'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}